/**
 * FileStatusPill — compact chip showing per-file ingest status.
 *
 * Covers the full ingest lifecycle:
 *   queued → extracting → ocr-retry → ready | failed
 *
 * Color mapping intentionally uses high-contrast combos legible on both
 * dark surfaces (surface-2/#18181b) and light backgrounds.
 */

import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/utils'
import type { BatchItemStatus } from '../ingest-api-client'

interface FileStatusPillProps {
  status: BatchItemStatus
  /** Pass true when rendered on a dark background (default: false) */
  isDark?: boolean
  className?: string
}

/** Visual config per status */
const STATUS_STYLES: Record<
  BatchItemStatus,
  { dark: string; light: string; dot: string }
> = {
  queued: {
    dark: 'bg-neutral-700/60 text-neutral-300',
    light: 'bg-neutral-100 text-neutral-600',
    dot: 'bg-neutral-400',
  },
  extracting: {
    dark: 'bg-brand-900/60 text-brand-300',
    light: 'bg-brand-50 text-brand-700',
    dot: 'bg-brand-400 animate-pulse',
  },
  'ocr-retry': {
    dark: 'bg-amber-900/40 text-amber-300',
    light: 'bg-amber-50 text-amber-700',
    dot: 'bg-amber-400 animate-pulse',
  },
  ready: {
    dark: 'bg-emerald-900/40 text-emerald-300',
    light: 'bg-emerald-50 text-emerald-700',
    dot: 'bg-emerald-400',
  },
  failed: {
    dark: 'bg-red-900/40 text-red-300',
    light: 'bg-red-50 text-red-700',
    dot: 'bg-red-400',
  },
}

/** Maps status to the i18n key inside the `status` namespace */
const STATUS_I18N_KEY: Record<BatchItemStatus, string> = {
  queued: 'status.queued',
  extracting: 'status.extracting',
  'ocr-retry': 'status.ocr_retry',
  ready: 'status.ready',
  failed: 'status.failed',
}

export function FileStatusPill({
  status,
  isDark = false,
  className,
}: FileStatusPillProps) {
  const { t } = useTranslation('ingest')
  const styles = STATUS_STYLES[status]
  const label = t(STATUS_I18N_KEY[status])

  return (
    <span
      role="status"
      aria-label={label}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium',
        isDark ? styles.dark : styles.light,
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn('h-1.5 w-1.5 rounded-full flex-shrink-0', styles.dot)}
      />
      {label}
    </span>
  )
}
